import type {
  CheckIn,
  KeyResult,
  Objective,
  PriorityT,
  SelectedTaskArrangement,
  SmartList,
  Task,
  TaskActivity,
  TaskArrangement,
  WorkflowColumn,
} from '@portfolio-pm/shared';
import {
  getCheckIn,
  getCheckInsMap,
  getKeyResult,
  getKeyResultsMap,
  getObjective,
  getObjectivesMap,
  getSelectedTaskArrangement,
  getSelectedTaskArrangementsMap,
  getSmartList,
  getSmartListsMap,
  getTask,
  getTaskActivitiesMap,
  getTaskActivity,
  getTaskArrangement,
  getTaskArrangementsMap,
  getTasksMap,
  getWorkflowColumn,
  getWorkflowColumnsMap,
} from '@portfolio-pm/yjs-doc';
import type * as Y from 'yjs';
import * as rollups from './rollups.ts';
import type { RollupSource } from './rollups.ts';

/**
 * In-memory projection over a Y.Doc's collections.
 *
 * The UI consumes this projection through useSyncExternalStore wrappers in the
 * doc store. It keeps indexed read paths for tasks and OKRs while preserving
 * reactive updates from Yjs.
 */
export interface Projection {
  getTask(id: string): Task | undefined;
  getTasks(): Task[];
  getOpenTasks(): Task[];
  byProject(projectId: string): Task[];
  byTag(tag: string): Task[];
  byPriority(priority: PriorityT): Task[];
  dueOnDay(utcDayStart: number): Task[];
  tasksByKeyResult(keyResultId: string): Task[];
  childTasks(parentTaskId: string): Task[];
  hasChildTasks(taskId: string): boolean;
  isTaskBlocked(taskId: string): boolean;

  getObjective(id: string): Objective | undefined;
  getObjectives(): Objective[];
  objectivesByProject(projectId: string): Objective[];
  rootObjectivesByProject(projectId: string): Objective[];
  childObjectives(parentObjectiveId: string): Objective[];

  getKeyResult(id: string): KeyResult | undefined;
  getKeyResults(): KeyResult[];
  keyResultsByObjective(objectiveId: string): KeyResult[];

  getCheckIns(): CheckIn[];
  checkInsForTarget(targetType: CheckIn['targetType'], targetId: string, limit?: number): CheckIn[];

  getWorkflowColumns(): WorkflowColumn[];
  getSmartLists(): SmartList[];
  getTaskArrangements(): TaskArrangement[];
  getSelectedTaskArrangements(): SelectedTaskArrangement[];
  getTaskActivities(): TaskActivity[];
  taskActivitiesForTask(taskId: string, limit?: number): TaskActivity[];

  keyResultProgress(keyResultId: string): number;
  expectedKeyResultProgress(keyResultId: string, now: number): number;
  keyResultLag(keyResultId: string, now: number): number;
  objectiveProgress(objectiveId: string): number;
  expectedObjectiveProgress(objectiveId: string, now: number): number;
  objectiveLag(objectiveId: string, now: number): number;

  subscribe(listener: () => void): () => void;
  destroy(): void;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ROOT_PARENT_KEY = '__root__';

function startOfDayUtc(ms: number): number {
  return ms - (ms % MS_PER_DAY);
}

export function createProjection(doc: Y.Doc): Projection {
  const tasksMap = getTasksMap(doc);
  const objectivesMap = getObjectivesMap(doc);
  const keyResultsMap = getKeyResultsMap(doc);
  const checkInsMap = getCheckInsMap(doc);
  const workflowColumnsMap = getWorkflowColumnsMap(doc);
  const smartListsMap = getSmartListsMap(doc);
  const taskArrangementsMap = getTaskArrangementsMap(doc);
  const selectedTaskArrangementsMap = getSelectedTaskArrangementsMap(doc);
  const taskActivitiesMap = getTaskActivitiesMap(doc);

  const tasksState = new Map<string, Task>();
  const objectivesState = new Map<string, Objective>();
  const keyResultsState = new Map<string, KeyResult>();
  const checkInsState = new Map<string, CheckIn>();
  const workflowColumnsState = new Map<string, WorkflowColumn>();
  const smartListsState = new Map<string, SmartList>();
  const taskArrangementsState = new Map<string, TaskArrangement>();
  const selectedTaskArrangementsState = new Map<string, SelectedTaskArrangement>();
  const taskActivitiesState = new Map<string, TaskActivity>();

  const byProjectIdx = new Map<string, Set<string>>();
  const byTagIdx = new Map<string, Set<string>>();
  const byPriorityIdx = new Map<PriorityT, Set<string>>();
  const byDueDayIdx = new Map<number, Set<string>>();
  const byTaskKrIdx = new Map<string, Set<string>>();
  const byParentTaskIdx = new Map<string, Set<string>>();

  const byObjectiveProjectIdx = new Map<string, Set<string>>();
  const byObjectiveParentIdx = new Map<string, Set<string>>();
  const byKeyResultObjectiveIdx = new Map<string, Set<string>>();
  const byCheckInTargetIdx = new Map<string, Set<string>>();
  const byTaskActivityIdx = new Map<string, Set<string>>();

  const listeners = new Set<() => void>();
  let destroyed = false;
  let notifyPending = false;

  // Notification coalescing: each map observer marks the projection dirty via
  // scheduleNotify() instead of notifying listeners directly. A single Y.Doc
  // 'afterTransaction' handler flushes the flag once per (outermost)
  // transaction, after every observer has already updated its indexes — Yjs
  // emits 'afterTransaction' after all type observers have run. This keeps
  // notification synchronous (listeners fire in the same tick as the write,
  // so useSyncExternalStore snapshots stay consistent) while collapsing a
  // transaction that touches N observed maps into ONE listener pass.
  const scheduleNotify = (): void => {
    notifyPending = true;
  };

  const flushNotify = (): void => {
    if (!notifyPending || destroyed) return;
    notifyPending = false;
    for (const listener of listeners) listener();
  };

  doc.on('afterTransaction', flushNotify);

  const addToIndex = <K>(index: Map<K, Set<string>>, key: K, id: string): void => {
    let bucket = index.get(key);
    if (!bucket) {
      bucket = new Set();
      index.set(key, bucket);
    }
    bucket.add(id);
  };

  const removeFromIndex = <K>(index: Map<K, Set<string>>, key: K, id: string): void => {
    const bucket = index.get(key);
    if (!bucket) return;
    bucket.delete(id);
    if (bucket.size === 0) index.delete(key);
  };

  const parentKey = (parentObjectiveId: string | undefined): string =>
    parentObjectiveId ?? ROOT_PARENT_KEY;

  const targetKey = (targetType: CheckIn['targetType'], targetId: string): string =>
    `${targetType}:${targetId}`;

  const indexTask = (task: Task): void => {
    addToIndex(byProjectIdx, task.projectId, task.id);
    addToIndex(byPriorityIdx, task.priority, task.id);
    for (const tag of task.tags) addToIndex(byTagIdx, tag, task.id);
    if (task.dueAt !== undefined) addToIndex(byDueDayIdx, startOfDayUtc(task.dueAt), task.id);
    if (task.krId !== undefined) addToIndex(byTaskKrIdx, task.krId, task.id);
    if (task.parentTaskId !== undefined) addToIndex(byParentTaskIdx, task.parentTaskId, task.id);
  };

  const unindexTask = (task: Task): void => {
    removeFromIndex(byProjectIdx, task.projectId, task.id);
    removeFromIndex(byPriorityIdx, task.priority, task.id);
    for (const tag of task.tags) removeFromIndex(byTagIdx, tag, task.id);
    if (task.dueAt !== undefined) removeFromIndex(byDueDayIdx, startOfDayUtc(task.dueAt), task.id);
    if (task.krId !== undefined) removeFromIndex(byTaskKrIdx, task.krId, task.id);
    if (task.parentTaskId !== undefined)
      removeFromIndex(byParentTaskIdx, task.parentTaskId, task.id);
  };

  const indexObjective = (objective: Objective): void => {
    addToIndex(byObjectiveProjectIdx, objective.projectId, objective.id);
    addToIndex(byObjectiveParentIdx, parentKey(objective.parentObjectiveId), objective.id);
  };

  const unindexObjective = (objective: Objective): void => {
    removeFromIndex(byObjectiveProjectIdx, objective.projectId, objective.id);
    removeFromIndex(byObjectiveParentIdx, parentKey(objective.parentObjectiveId), objective.id);
  };

  const indexKeyResult = (keyResult: KeyResult): void => {
    addToIndex(byKeyResultObjectiveIdx, keyResult.objectiveId, keyResult.id);
  };

  const unindexKeyResult = (keyResult: KeyResult): void => {
    removeFromIndex(byKeyResultObjectiveIdx, keyResult.objectiveId, keyResult.id);
  };

  const indexCheckIn = (checkIn: CheckIn): void => {
    addToIndex(byCheckInTargetIdx, targetKey(checkIn.targetType, checkIn.targetId), checkIn.id);
  };

  const unindexCheckIn = (checkIn: CheckIn): void => {
    removeFromIndex(
      byCheckInTargetIdx,
      targetKey(checkIn.targetType, checkIn.targetId),
      checkIn.id,
    );
  };

  const indexTaskActivity = (activity: TaskActivity): void => {
    addToIndex(byTaskActivityIdx, activity.taskId, activity.id);
  };

  const unindexTaskActivity = (activity: TaskActivity): void => {
    removeFromIndex(byTaskActivityIdx, activity.taskId, activity.id);
  };

  const removeTaskFromState = (id: string): void => {
    const prev = tasksState.get(id);
    if (!prev) return;
    unindexTask(prev);
    tasksState.delete(id);
  };

  const upsertTask = (id: string): void => {
    const next = getTask(doc, id);
    if (!next) {
      removeTaskFromState(id);
      return;
    }
    const prev = tasksState.get(id);
    if (prev) unindexTask(prev);
    tasksState.set(id, next);
    indexTask(next);
  };

  const removeObjectiveFromState = (id: string): void => {
    const prev = objectivesState.get(id);
    if (!prev) return;
    unindexObjective(prev);
    objectivesState.delete(id);
  };

  const upsertObjective = (id: string): void => {
    const next = getObjective(doc, id);
    if (!next) {
      removeObjectiveFromState(id);
      return;
    }
    const prev = objectivesState.get(id);
    if (prev) unindexObjective(prev);
    objectivesState.set(id, next);
    indexObjective(next);
  };

  const removeKeyResultFromState = (id: string): void => {
    const prev = keyResultsState.get(id);
    if (!prev) return;
    unindexKeyResult(prev);
    keyResultsState.delete(id);
  };

  const upsertKeyResult = (id: string): void => {
    const next = getKeyResult(doc, id);
    if (!next) {
      removeKeyResultFromState(id);
      return;
    }
    const prev = keyResultsState.get(id);
    if (prev) unindexKeyResult(prev);
    keyResultsState.set(id, next);
    indexKeyResult(next);
  };

  const removeCheckInFromState = (id: string): void => {
    const prev = checkInsState.get(id);
    if (!prev) return;
    unindexCheckIn(prev);
    checkInsState.delete(id);
  };

  const upsertCheckIn = (id: string): void => {
    const next = getCheckIn(doc, id);
    if (!next) {
      removeCheckInFromState(id);
      return;
    }
    const prev = checkInsState.get(id);
    if (prev) unindexCheckIn(prev);
    checkInsState.set(id, next);
    indexCheckIn(next);
  };

  const removeWorkflowColumnFromState = (id: string): void => {
    workflowColumnsState.delete(id);
  };

  const upsertWorkflowColumn = (id: string): void => {
    const next = getWorkflowColumn(doc, id);
    if (!next) {
      removeWorkflowColumnFromState(id);
      return;
    }
    workflowColumnsState.set(id, next);
  };

  const removeSmartListFromState = (id: string): void => {
    smartListsState.delete(id);
  };

  const upsertSmartList = (id: string): void => {
    const next = getSmartList(doc, id);
    if (!next) {
      removeSmartListFromState(id);
      return;
    }
    smartListsState.set(id, next);
  };

  const removeTaskArrangementFromState = (id: string): void => {
    taskArrangementsState.delete(id);
  };

  const upsertTaskArrangement = (id: string): void => {
    const next = getTaskArrangement(doc, id);
    if (!next) {
      removeTaskArrangementFromState(id);
      return;
    }
    taskArrangementsState.set(id, next);
  };

  const removeSelectedTaskArrangementFromState = (listKey: string): void => {
    selectedTaskArrangementsState.delete(listKey);
  };

  const upsertSelectedTaskArrangement = (listKey: string): void => {
    const next = getSelectedTaskArrangement(doc, listKey);
    if (!next) {
      removeSelectedTaskArrangementFromState(listKey);
      return;
    }
    selectedTaskArrangementsState.set(listKey, next);
  };

  const removeTaskActivityFromState = (id: string): void => {
    const prev = taskActivitiesState.get(id);
    if (!prev) return;
    unindexTaskActivity(prev);
    taskActivitiesState.delete(id);
  };

  const upsertTaskActivity = (id: string): void => {
    const next = getTaskActivity(doc, id);
    if (!next) {
      removeTaskActivityFromState(id);
      return;
    }
    const prev = taskActivitiesState.get(id);
    if (prev) unindexTaskActivity(prev);
    taskActivitiesState.set(id, next);
    indexTaskActivity(next);
  };

  const resolveBucket = <T>(ids: Set<string> | undefined, state: Map<string, T>): T[] => {
    if (!ids) return [];
    const out: T[] = [];
    for (const id of ids) {
      const value = state.get(id);
      if (value) out.push(value);
    }
    return out;
  };

  const sortedByOrder = <T extends { order: number }>(items: T[]): T[] =>
    [...items].sort((a, b) => a.order - b.order);

  const sortedCheckIns = (items: CheckIn[]): CheckIn[] =>
    [...items].sort((a, b) => b.createdAt - a.createdAt);

  const sortedTaskActivities = (items: TaskActivity[]): TaskActivity[] =>
    [...items].sort((a, b) => b.createdAt - a.createdAt);

  const isTaskBlocked = (taskId: string): boolean => {
    const task = tasksState.get(taskId);
    if (!task?.dependsOn?.length) return false;
    return task.dependsOn.some((dependencyId) => {
      const dependency = tasksState.get(dependencyId);
      return dependency !== undefined && dependency.completedAt === undefined;
    });
  };

  // Rollup maths lives in ./rollups.ts so the Sync Durable Object can produce
  // identical numbers from a bounded, document-backed source without paying to
  // build a whole-workspace projection (REQ-API-002).
  const rollupSource: RollupSource = {
    getObjective: (id) => objectivesState.get(id),
    getKeyResult: (id) => keyResultsState.get(id),
    keyResultsByObjective: (objectiveId) =>
      resolveBucket(byKeyResultObjectiveIdx.get(objectiveId), keyResultsState),
    childObjectives: (parentObjectiveId) =>
      resolveBucket(byObjectiveParentIdx.get(parentObjectiveId), objectivesState),
    tasksByKeyResult: (keyResultId) => resolveBucket(byTaskKrIdx.get(keyResultId), tasksState),
  };

  const keyResultProgress = (keyResultId: string): number =>
    rollups.keyResultProgress(rollupSource, keyResultId);

  const objectiveProgress = (objectiveId: string): number =>
    rollups.objectiveProgress(rollupSource, objectiveId);

  const expectedObjectiveProgress = (objectiveId: string, now: number): number =>
    rollups.expectedObjectiveProgress(rollupSource, objectiveId, now);

  const objectiveLag = (objectiveId: string, now: number): number =>
    rollups.objectiveLag(rollupSource, objectiveId, now);

  const expectedKeyResultProgress = (keyResultId: string, now: number): number =>
    rollups.expectedKeyResultProgress(rollupSource, keyResultId, now);

  const keyResultLag = (keyResultId: string, now: number): number =>
    rollups.keyResultLag(rollupSource, keyResultId, now);

  const onMapChanged = (
    map: Y.Map<Y.Map<unknown>>,
    upsert: (id: string) => void,
    events: Y.YEvent<Y.AbstractType<unknown>>[],
  ): void => {
    const touched = new Set<string>();
    for (const event of events) {
      if (event.target === map) {
        for (const id of event.changes.keys.keys()) touched.add(id);
      } else {
        const id = event.path[0];
        if (typeof id === 'string') touched.add(id);
      }
    }
    for (const id of touched) upsert(id);
    scheduleNotify();
  };

  // Seed initial state.
  for (const id of tasksMap.keys()) upsertTask(id);
  for (const id of objectivesMap.keys()) upsertObjective(id);
  for (const id of keyResultsMap.keys()) upsertKeyResult(id);
  for (const id of checkInsMap.keys()) upsertCheckIn(id);
  for (const id of workflowColumnsMap.keys()) upsertWorkflowColumn(id);
  for (const id of smartListsMap.keys()) upsertSmartList(id);
  for (const id of taskArrangementsMap.keys()) upsertTaskArrangement(id);
  for (const listKey of selectedTaskArrangementsMap.keys()) upsertSelectedTaskArrangement(listKey);
  for (const id of taskActivitiesMap.keys()) upsertTaskActivity(id);

  const tasksObserver = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    onMapChanged(tasksMap, upsertTask, events);
  };
  const objectivesObserver = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    onMapChanged(objectivesMap, upsertObjective, events);
  };
  const keyResultsObserver = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    onMapChanged(keyResultsMap, upsertKeyResult, events);
  };
  const checkInsObserver = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    onMapChanged(checkInsMap, upsertCheckIn, events);
  };
  const workflowColumnsObserver = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    onMapChanged(workflowColumnsMap, upsertWorkflowColumn, events);
  };
  const smartListsObserver = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    onMapChanged(smartListsMap, upsertSmartList, events);
  };
  const taskArrangementsObserver = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    onMapChanged(taskArrangementsMap, upsertTaskArrangement, events);
  };
  const selectedTaskArrangementsObserver = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    onMapChanged(selectedTaskArrangementsMap, upsertSelectedTaskArrangement, events);
  };
  const taskActivitiesObserver = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    onMapChanged(taskActivitiesMap, upsertTaskActivity, events);
  };

  tasksMap.observeDeep(tasksObserver);
  objectivesMap.observeDeep(objectivesObserver);
  keyResultsMap.observeDeep(keyResultsObserver);
  checkInsMap.observeDeep(checkInsObserver);
  workflowColumnsMap.observeDeep(workflowColumnsObserver);
  smartListsMap.observeDeep(smartListsObserver);
  taskArrangementsMap.observeDeep(taskArrangementsObserver);
  selectedTaskArrangementsMap.observeDeep(selectedTaskArrangementsObserver);
  taskActivitiesMap.observeDeep(taskActivitiesObserver);

  return {
    getTask: (id) => tasksState.get(id),
    getTasks: () => [...tasksState.values()],
    getOpenTasks: () =>
      [...tasksState.values()].filter(
        (task) => task.completedAt === undefined && task.trashedAt === undefined,
      ),
    byProject: (projectId) => resolveBucket(byProjectIdx.get(projectId), tasksState),
    byTag: (tag) => resolveBucket(byTagIdx.get(tag), tasksState),
    byPriority: (priority) => resolveBucket(byPriorityIdx.get(priority), tasksState),
    dueOnDay: (utcDayStart) =>
      resolveBucket(byDueDayIdx.get(startOfDayUtc(utcDayStart)), tasksState),
    tasksByKeyResult: (keyResultId) => resolveBucket(byTaskKrIdx.get(keyResultId), tasksState),
    childTasks: (parentTaskId) => resolveBucket(byParentTaskIdx.get(parentTaskId), tasksState),
    hasChildTasks: (taskId) => (byParentTaskIdx.get(taskId)?.size ?? 0) > 0,
    isTaskBlocked,

    getObjective: (id) => objectivesState.get(id),
    getObjectives: () => sortedByOrder([...objectivesState.values()]),
    objectivesByProject: (projectId) =>
      sortedByOrder(resolveBucket(byObjectiveProjectIdx.get(projectId), objectivesState)),
    rootObjectivesByProject: (projectId) =>
      sortedByOrder(
        resolveBucket(byObjectiveProjectIdx.get(projectId), objectivesState).filter(
          (objective) => objective.parentObjectiveId === undefined,
        ),
      ),
    childObjectives: (parentObjectiveId) =>
      sortedByOrder(resolveBucket(byObjectiveParentIdx.get(parentObjectiveId), objectivesState)),

    getKeyResult: (id) => keyResultsState.get(id),
    getKeyResults: () => sortedByOrder([...keyResultsState.values()]),
    keyResultsByObjective: (objectiveId) =>
      sortedByOrder(resolveBucket(byKeyResultObjectiveIdx.get(objectiveId), keyResultsState)),

    getCheckIns: () => sortedCheckIns([...checkInsState.values()]),
    checkInsForTarget: (targetType, targetId, limit = 20) =>
      sortedCheckIns(
        resolveBucket(byCheckInTargetIdx.get(targetKey(targetType, targetId)), checkInsState),
      ).slice(0, limit),

    getWorkflowColumns: () => sortedByOrder([...workflowColumnsState.values()]),
    getSmartLists: () => sortedByOrder([...smartListsState.values()]),
    getTaskArrangements: () => sortedByOrder([...taskArrangementsState.values()]),
    getSelectedTaskArrangements: () =>
      [...selectedTaskArrangementsState.values()].sort((a, b) =>
        a.listKey.localeCompare(b.listKey),
      ),
    getTaskActivities: () => sortedTaskActivities([...taskActivitiesState.values()]),
    taskActivitiesForTask: (taskId, limit = 20) =>
      sortedTaskActivities(resolveBucket(byTaskActivityIdx.get(taskId), taskActivitiesState)).slice(
        0,
        limit,
      ),

    keyResultProgress,
    expectedKeyResultProgress,
    keyResultLag,
    objectiveProgress,
    expectedObjectiveProgress,
    objectiveLag,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      notifyPending = false;
      doc.off('afterTransaction', flushNotify);
      tasksMap.unobserveDeep(tasksObserver);
      objectivesMap.unobserveDeep(objectivesObserver);
      keyResultsMap.unobserveDeep(keyResultsObserver);
      checkInsMap.unobserveDeep(checkInsObserver);
      workflowColumnsMap.unobserveDeep(workflowColumnsObserver);
      smartListsMap.unobserveDeep(smartListsObserver);
      taskArrangementsMap.unobserveDeep(taskArrangementsObserver);
      selectedTaskArrangementsMap.unobserveDeep(selectedTaskArrangementsObserver);
      taskActivitiesMap.unobserveDeep(taskActivitiesObserver);
      listeners.clear();
      tasksState.clear();
      objectivesState.clear();
      keyResultsState.clear();
      checkInsState.clear();
      workflowColumnsState.clear();
      smartListsState.clear();
      taskArrangementsState.clear();
      selectedTaskArrangementsState.clear();
      taskActivitiesState.clear();
      byProjectIdx.clear();
      byTagIdx.clear();
      byPriorityIdx.clear();
      byDueDayIdx.clear();
      byTaskKrIdx.clear();
      byParentTaskIdx.clear();
      byObjectiveProjectIdx.clear();
      byObjectiveParentIdx.clear();
      byKeyResultObjectiveIdx.clear();
      byCheckInTargetIdx.clear();
      byTaskActivityIdx.clear();
    },
  };
}
